import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "react-redux";
import Note from "../../../model/note/Note";
import {
	INVOICE_NOTE_CREATED,
	INVOICE_NOTE_DELETED,
	INVOICE_NOTE_UPDATED,
	INVOICE_NOTES_FETCHED,
	JOB_NOTE_CREATED,
	JOB_NOTE_DELETED,
	JOB_NOTE_REMINDER_ADDED,
	JOB_NOTE_UPDATED,
	JOB_NOTES_FETCHED,
	NOTE_CREATED,
	NOTE_DELETED,
	NOTE_REMINDER_ADDED,
	NOTE_UPDATED,
	NOTES_FETCHED
} from "./actionType";
import {api} from "../../../api/api";
import * as moment from "moment";
import {Action} from "redux-actions";
import {fetchLead} from "../lead/lead";
import {fetchJob} from "../job/job";
import AccountingNote from "../../../model/invoice/note/AccountingNote";


/**
 * Notes fetched successful action.
 * @param {string} id lead identifier.
 * @param {Note[]} notes
 * @return {Action<NOTES_FETCHED>}
 */
function notesFetched(id: string, notes: Note[]) {
	return {type: NOTES_FETCHED, payload: {leadId: id, notes}};
}

/**
 * Note created successful action.
 * @param {id} id lead identifier.
 * @param {Note} note newly added note.
 * @return {Action<NOTE_CREATED>}
 */
function noteCreated(id: string, note: Note) {
	return {type: NOTE_CREATED, payload: {leadId: id, note}};
}

/**
 * Note updated successful action.
 * @param {string} id lead identifier.
 * @param {Note} note updated note.
 * @return {Action<NOTE_UPDATED>}
 */
function noteUpdated(id: string, note: Note) {
	return {type: NOTE_UPDATED, payload: {leadId: id, note: note}};
}


/**
 * Note reminder added successful action.
 * @param {string} id lead identifier.
 * @param {string} noteId note identifier.
 * @param {dateTime} reminderDate date of reminder.
 * @return {Action<NOTE_UPDATED>}
 */
function noteReminderAdded(id: string, noteId: string, reminderDate: moment.Moment): Action<NOTE_REMINDER_ADDED> {
	return {type: NOTE_REMINDER_ADDED, payload: {leadId: id, noteId: noteId, reminderDate}};
}

/**
 * Note deleted successful action.
 * @param {string} id lead identifier.
 * @param {string} noteId note identifier.
 * @return {Action<NOTE_DELETED>
 */
function noteDeleted(id: string, noteId: string) {
	return {type: NOTE_DELETED, payload: {leadId: id, noteId}};
}

/**
 * Job notes fetched successful action.
 * @param {string} id job identifier.
 * @param {Note[]} notes
 * @return {Action<JOB_NOTES_FETCHED>}
 */
function jobNotesFetched(id: string, notes: Note[]) {
	return {type: JOB_NOTES_FETCHED, payload: {jobId: id, notes}};
}

/**
 * Job note created successful action.
 * @param {id} id job identifier.
 * @param {Note} note newly added note.
 * @return {Action<JOB_NOTE_CREATED>}
 */
function jobNoteCreated(id: string, note: Note) {
	return {type: JOB_NOTE_CREATED, payload: {jobId: id, note}};
}

/**
 * Job note updated successful action.
 * @param {string} id job identifier.
 * @param {Note} note updated note.
 * @return {Action<JOB_NOTE_UPDATED>}
 */
function jobNoteUpdated(id: string, note: Note) {
	return {type: JOB_NOTE_UPDATED, payload: {jobId: id, note: note}};
}


/**
 * Job note deleted successful action.
 * @param {string} id job identifier.
 * @param {string} noteId note identifier.
 * @return {Action<JOB_NOTE_DELETED>
 */
function jobNoteDeleted(id: string, noteId: string) {
	return {type: JOB_NOTE_DELETED, payload: {jobId: id, noteId}};
}

/**
 * Note reminder added successful action.
 * @param {string} jobId job identifier.
 * @param {string} noteId note identifier.
 * @param {dateTime} reminderDate date of reminder.
 * @return {Action<NOTE_UPDATED>}
 */
function jobNoteReminderAdded(jobId: string, noteId: string, reminderDate: moment.Moment): Action<JOB_NOTE_REMINDER_ADDED> {
	return {type: JOB_NOTE_REMINDER_ADDED, payload: {jobId, noteId, reminderDate}};
}

/**
 * Invoice notes fetched successful action.
 * @param invoiceId
 * @param {AccountingNote[]} notes
 * @return {Action<JOB_NOTES_FETCHED>}
 */
function invoiceNotesFetched(invoiceId: string, notes: AccountingNote[]) {
	return {type: INVOICE_NOTES_FETCHED, payload: {invoiceId, notes}};
}

/**
 * Invoice note created successful action.
 * @param invoiceId
 * @param {AccountingNote} note newly added note.
 * @return {Action<JOB_NOTE_CREATED>}
 */
function invoiceNoteCreated(invoiceId: string, note: AccountingNote) {
	return {type: INVOICE_NOTE_CREATED, payload: {invoiceId, note}};
}

/**
 * Invoice note updated successful action.
 * @param invoiceId
 * @param {AccountingNote} note updated note.
 * @return {Action<JOB_NOTE_UPDATED>}
 */
function invoiceNoteUpdated(invoiceId: string, note: AccountingNote) {
	return {type: INVOICE_NOTE_UPDATED, payload: {invoiceId, note: note}};
}

/**
 * Invoice note deleted successful action.
 * @param invoiceId
 * @param {string} noteId note identifier.
 * @return {Action<JOB_NOTE_DELETED>
 */
function invoiceNoteDeleted(invoiceId: string, noteId: string) {
	return {type: INVOICE_NOTE_DELETED, payload: {invoiceId, noteId}};
}

/**
 * Fetch notes action
 * @param {string} leadId lead identifier.
 * @param {OnSuccessData<Note[]>} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure fetch action callback.
 */
export function fetchNotes(leadId: string, onSuccess: OnSuccessData<Note[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.fetchNotes(leadId).then(notes => {
			dispatch(notesFetched(leadId, notes));
			onSuccess(notes);
		})
	}
}

/**
 * Create lead note action.
 * @param {string} leadId lead identifier.
 * @param {string} content note content.
 * @param {string} extendedContent extended content to set.
 * @param {boolean} pinned is note pinned flag.
 * @param {OnSuccessData<Note>} onSuccess on successful creation action callback.
 * @param {OnFailure} onFailure on failure creation action callback.
 */
export function createNote(leadId: string,
	content: string,
	extendedContent: string,
	pinned: boolean,
	onSuccess: OnSuccessData<Note>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.createNote(leadId, {content, extendedContent, pinned}).then(note => {
			dispatch(noteCreated(leadId, note));
			dispatch(fetchLead(leadId, () => {}, () => {}));
			onSuccess(note);
		}).catch(onFailure);
	}
}

/**
 * Update lead note action.
 * @param {string} noteId note identifier.
 * @param {string} leadId lead identifier.
 * @param {string} content content to set.
 * @param {string} extendedContent extended content to set.
 * @param {OnSuccess} onSuccess on successful update action callback.
 * @param {OnFailure} onFailure on failure update action callback.
 */
export function updateNote(noteId: string,
	leadId: string,
	content: string,
	extendedContent: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.updateNote(noteId, {content, extendedContent}).then(note => {
			dispatch(noteUpdated(leadId, note));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Delete lead note action.
 * @param {string} noteId note identifier.
 * @param {string} leadId lead identifier.
 * @param {OnSuccess} onSuccess on successful delete action callback.
 * @param {OnFailure} onFailure on failure delete action callback.
 */
export function deleteNote(noteId: string, leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.deleteNote(noteId).then(() => {
			dispatch(noteDeleted(leadId, noteId));
			onSuccess();
		})
	}
}

/**
 * Add note reminder action.
 * @param {string} noteId note identifier.
 * @param {string} leadId lead identifier.
 * @param {reminderDate} reminderDate note reminder date to set.
 * @param {OnSuccessData<Note>} onSuccess on successful update action callback.
 * @param {OnFailure} onFailure on failure update action callback.
 */
export function addReminder(noteId: string, leadId: string, reminderDate: moment.Moment, onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {

		api.instance.noteApi.addReminder(leadId, noteId, {dateTime: reminderDate.format()}).then(() => {
			dispatch(noteReminderAdded(leadId, noteId, reminderDate));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Fetch job notes action
 * @param {string} jobId job identifier.
 * @param {OnSuccessData<Note[]>} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure fetch action callback.
 */
export function fetchJobNotes(jobId: string, onSuccess: OnSuccessData<Note[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<JOB_NOTES_FETCHED>) {
		api.instance.noteApi.fetchJobNotes(jobId).then(notes => {
			dispatch(jobNotesFetched(jobId, notes));
			onSuccess(notes);
		})
	}
}

/**
 * Create job note action.
 * @param {string} jobId job identifier.
 * @param {string} content note content.
 * @param {string} extendedContent extended content to set.
 * @param {boolean} pinned is note pinned flag.
 * @param {OnSuccessData<Note>} onSuccess on successful creation action callback.
 * @param {OnFailure} onFailure on failure creation action callback.
 */
export function createJobNote(jobId: string,
	content: string,
	extendedContent: string,
	pinned: boolean,
	onSuccess: OnSuccessData<Note>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.createJobNote(jobId, {content, extendedContent, pinned}).then(note => {
			dispatch(jobNoteCreated(jobId, note));
			dispatch(fetchJob(jobId, () => {}, () => {}));
			onSuccess(note);
		});
	}
}

/**
 * Update job note action.
 * @param {string} noteId note identifier.
 * @param {string} jobId job identifier.
 * @param {string} content content to set.
 * @param {string} extendedContent extended content to set.
 * @param {OnSuccess} onSuccess on successful update action callback.
 * @param {OnFailure} onFailure on failure update action callback.
 */
export function updateJobNote(noteId: string,
	jobId: string,
	content: string,
	extendedContent: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.updateJobNote(jobId, noteId, {content, extendedContent}).then(note => {
			dispatch(jobNoteUpdated(jobId, note));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Delete job note action.
 * @param {string} noteId note identifier.
 * @param {string} jobId job identifier.
 * @param {OnSuccess} onSuccess on successful delete action callback.
 * @param {OnFailure} onFailure on failure delete action callback.
 */
export function deleteJobNote(noteId: string, jobId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.deleteJobNote(jobId, noteId).then(() => {
			dispatch(jobNoteDeleted(jobId, noteId));
			onSuccess();
		})
	}
}

/**
 * Modify pinned flag on job note
 * @param {string} noteId note identifier.
 * @param {string} jobId job identifier.
 * @param {boolean} pinned is note pinned flag.
 * @param {OnSuccess} onSuccess on successful delete action callback.
 * @param {OnFailure} onFailure on failure delete action callback.
 */
export function modifyPinningOnJobNote(noteId: string,
	jobId: string,
	pinned: boolean,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.modifyPinningOnJobNote(jobId, noteId, pinned).then((note) => {
			dispatch(jobNoteUpdated(jobId, note));
			onSuccess();
		});
	}
}

/**
 * Modify pinned flag on lead note
 * @param {string} noteId note identifier.
 * @param {string} leadId lead identifier.
 * @param {boolean} pinned is note pinned flag.
 * @param {OnSuccess} onSuccess on successful delete action callback.
 * @param {OnFailure} onFailure on failure delete action callback.
 */
export function modifyPinningOnLeadNote(noteId: string,
	leadId: string,
	pinned: boolean,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.modifyPinningOnNote(noteId, pinned).then((note) => {
			dispatch(noteUpdated(leadId, note));
			onSuccess();
		});
	}
}

/**
 * Add job note reminder action.
 * @param {string} noteId note identifier.
 * @param {string} jobId lead identifier.
 * @param {reminderDate} reminderDate note reminder date to set
 * @param {OnSuccessData<Note>} onSuccess on successful update action callback.
 * @param {OnFailure} onFailure on failure update action callback.
 */
export function addJobNoteReminder(noteId: string, jobId: string, reminderDate: moment.Moment, onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.addJobReminder(jobId, noteId, {dateTime: reminderDate.format()}).then(() => {
			dispatch(jobNoteReminderAdded(jobId, noteId, reminderDate));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Fetch job notes action
 * @param invoiceId
 * @param {OnSuccessData<Note[]>} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure fetch action callback.
 */
export function fetchInvoiceNotes(invoiceId: string, onSuccess: OnSuccessData<AccountingNote[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<JOB_NOTES_FETCHED>) {
		api.instance.noteApi.fetchInvoiceNotes(invoiceId).then(notes => {
			dispatch(invoiceNotesFetched(invoiceId, notes));
			onSuccess(notes);
		})
	}
}

/**
 * Create invoice note action.
 * @param {string} invoiceId invoice identifier.
 * @param {string} content note content.
 * @param {OnSuccessData<AccountingNote>} onSuccess on successful creation action callback.
 * @param {OnFailure} onFailure on failure creation action callback.
 */
export function createInvoiceNote(invoiceId: string,
	content: string,
	onSuccess: OnSuccessData<AccountingNote>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.createInvoiceNote(invoiceId, content).then(note => {
			dispatch(invoiceNoteCreated(invoiceId, note));
			onSuccess(note);
		});
	}
}

/**
 * Update invoice note action.
 * @param {string} noteId note identifier.
 * @param invoiceId
 * @param {string} content content to set.
 * @param {OnSuccess} onSuccess on successful update action callback.
 * @param {OnFailure} onFailure on failure update action callback.
 */
export function updateInvoiceNote(noteId: string,
	invoiceId: string,
	content: string,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.updateInvoiceNote(noteId, content).then(note => {
			dispatch(invoiceNoteUpdated(invoiceId, note));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Delete job note action.
 * @param {string} noteId note identifier.
 * @param invoiceId
 * @param {OnSuccess} onSuccess on successful delete action callback.
 * @param {OnFailure} onFailure on failure delete action callback.
 */
export function deleteInvoiceNote(noteId: string, invoiceId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.noteApi.deleteInvoiceNote(noteId).then(() => {
			dispatch(invoiceNoteDeleted(invoiceId, noteId));
			onSuccess();
		})
	}
}
